/**
 * Mobile Email Verification Banner
 *
 * Mobile-optimized banner prompting users to verify their email address.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '../../stores/authStore';

export default function MobileEmailVerificationBanner() {
  const { t } = useTranslation('mobile');
  const user = useUser();
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // Don't show if user is verified or not logged in
  if (!user || user.emailVerified) {
    return null;
  }

  const handleResendEmail = async () => {
    setIsResending(true);
    setResendStatus('idle');

    try {
      const response = await fetch('/api/auth/verify-email/resend', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (response.ok) {
        setResendStatus('success');
      } else {
        setResendStatus('error');
      }
    } catch {
      setResendStatus('error');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <svg
            className="w-5 h-5 text-amber-600 dark:text-amber-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
            {t('emailVerification.title')}
          </p>
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">
            {t('emailVerification.description')}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <button
              onClick={handleResendEmail}
              disabled={isResending}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-amber-700 dark:text-amber-200 bg-amber-100 dark:bg-amber-800/50 active:bg-amber-200 dark:active:bg-amber-800 disabled:opacity-50 transition-colors"
            >
              {isResending ? (
                <>
                  <svg className="animate-spin -ml-0.5 mr-1.5 h-3 w-3" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('emailVerification.sending')}
                </>
              ) : (
                t('emailVerification.resend')
              )}
            </button>
            {resendStatus === 'success' && (
              <span className="text-xs text-green-600 dark:text-green-400">
                {t('emailVerification.sent')}
              </span>
            )}
            {resendStatus === 'error' && (
              <span className="text-xs text-red-600 dark:text-red-400">
                {t('emailVerification.failed')}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
